import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Subject, of} from 'rxjs';
import {catchError, finalize, switchMap, take, takeUntil} from 'rxjs/operators';
import {ContactsApiService} from '@features/contacts/store/contacts-api.service';
import {XatBotDataService} from '@features/xatbot/services/xat-bot-data.service';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {
  ChatMember, groupMembers, MEMBER_GROUPINGS, MemberGroup, MemberGrouping, roleRank, searchMembers, toChatMembers,
  XATBOT_MEMBER
} from '../../messenger/chat-members';
import {chatFaceStyle} from '../../messenger/chat-face';
import {MessengerFlags} from '../../messenger/messenger-flags';
import {Availability, MemberStatus} from '../../messenger/messenger.models';
import {MessengerPresencePort} from '../../messenger/presence.port';

/** The status filter's values (carbon's `MEMBER_FILTERS`): a status, or everyone. */
export type MemberStatusFilter = Availability | 'all';

/**
 * THE MEMBERS TAB (ITP-E-203-02): carbon's people directory in the Chat panel. Everyone active in the reader's company
 * from `Contact/AllContacts`, searchable by name or address, grouped by type, by role, A to Z or Z to A, with XatBot
 * pinned first. A colleague is handed to the host, which opens the 1:1; XatBot opens the existing XatBot. The status
 * dots and the status filter ride B-15: built in full, off in production until presence is real (Plan Q6).
 */
@Component({
  standalone: false,
  selector: 'chat-members-tab',
  templateUrl: './members-tab.component.html',
  styleUrls: ['./members-tab.component.scss']
})
export class MembersTabComponent implements OnInit, OnDestroy {
  /** Room under the last row for the New chat disc that floats over the tab. */
  @Input() clearForDisc = false;
  /** A colleague was clicked: the host opens (or reopens) the 1:1 with them. */
  @Output() memberPicked = new EventEmitter<ChatMember>();

  readonly groupings = MEMBER_GROUPINGS;
  readonly statusFilters: { value: MemberStatusFilter, label: string }[] = [
    {value: 'all', label: 'Everyone'},
    {value: 'online', label: 'Online'},
    {value: 'busy', label: 'Busy'},
    {value: 'away', label: 'Away'},
    {value: 'dnd', label: 'Do not disturb'},
    {value: 'offline', label: 'Offline'}
  ];
  readonly showPresence: boolean;
  readonly faceStyle = chatFaceStyle(44);

  search = '';
  grouping: MemberGrouping = 'type';
  /** The tab opens on Online (carbon), while presence is on. */
  status: MemberStatusFilter = 'online';
  state: 'loading' | 'error' | 'ready' = 'loading';
  groups: MemberGroup[] = [];
  statusCounts: Record<MemberStatusFilter, number> = {all: 0, online: 0, busy: 0, away: 0, dnd: 0, offline: 0};
  presence: Record<string, MemberStatus> = {};

  private people: ChatMember[] = [];
  private me: string | null = null;
  private readonly load$ = new Subject<void>();
  private readonly unsubscribe$ = new Subject<void>();

  constructor(private contactsApi: ContactsApiService,
              private securityStore: Store<SecurityState>,
              private presencePort: MessengerPresencePort,
              private xatBotDataService: XatBotDataService,
              messengerFlags: MessengerFlags) {
    this.showPresence = messengerFlags.isOn('B-15');
  }

  ngOnInit() {
    this.securityStore.pipe(select(getUserInfo), takeUntil(this.unsubscribe$)).subscribe(user => {
      this.me = user?.email || null;
      this.refresh();
    });
    this.load$.pipe(
      switchMap(() => {
        this.state = 'loading';
        return this.contactsApi.getAllSharedContacts().pipe(catchError(() => {
          this.state = 'error';
          return of(null);
        }));
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      if (this.state === 'error') {
        return;
      }
      this.people = toChatMembers(response);
      this.state = 'ready';
      this.readPresence();
      this.refresh();
    });
    this.load();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  load() {
    this.load$.next();
  }

  onSearch(text: string) {
    this.search = text || '';
    this.refresh();
  }

  onGrouping(grouping: MemberGrouping) {
    this.grouping = grouping;
    this.refresh();
  }

  onStatus(status: MemberStatusFilter) {
    this.status = status;
    this.refresh();
  }

  open(member: ChatMember) {
    if (member.isXatBot) {
      this.xatBotDataService.openXatbot();
      return;
    }
    this.memberPicked.emit(member);
  }

  /** The role shown on the right of line one; No role draws nothing (carbon). */
  roleOf(member: ChatMember): string {
    const role = roleRank(member.roles);
    return role === 'No role' ? '' : role;
  }

  statusOf(member: ChatMember): MemberStatus | null {
    if (!this.showPresence) {
      return null;
    }
    return member.isXatBot ? {availability: 'online'} : this.presence[(member.email || '').toLowerCase()] || null;
  }

  get statusLabel(): string {
    return this.statusFilters.find(option => option.value === this.status)?.label || '';
  }

  trackByGroup(index: number, group: MemberGroup): string {
    return group.id;
  }

  trackByEmail(index: number, member: ChatMember): string {
    return member.email;
  }

  /** B-15: one read of every listed colleague's status; the counts and the filter follow it. */
  private readPresence() {
    if (!this.showPresence || !this.people.length) {
      return;
    }
    this.presencePort.statuses(this.people.map(person => ({email: person.email, companyId: person.companyId}))).pipe(
      take(1),
      catchError(() => of({} as Record<string, MemberStatus>)),
      finalize(() => this.refresh()),
      takeUntil(this.unsubscribe$)
    ).subscribe(statuses => this.presence = statuses || {});
  }

  private availabilityOf(member: ChatMember): Availability {
    return this.presence[(member.email || '').toLowerCase()]?.availability || 'offline';
  }

  /** The groups on screen: the reader left out, the search and the status filter applied, XatBot always first. */
  private refresh() {
    const colleagues = searchMembers(this.people, '', this.me);
    if (this.showPresence) {
      const counts = {all: colleagues.length, online: 0, busy: 0, away: 0, dnd: 0, offline: 0};
      colleagues.forEach(person => counts[this.availabilityOf(person)]++);
      this.statusCounts = counts;
    }
    const filtered = this.showPresence && this.status !== 'all'
      ? colleagues.filter(person => this.availabilityOf(person) === this.status)
      : colleagues;
    this.groups = groupMembers(searchMembers([XATBOT_MEMBER, ...filtered], this.search, this.me), this.grouping);
  }
}
